import React, { FC, useMemo } from 'react';
import { Formik } from 'formik';
import _isEqualWith from 'lodash/isEqualWith';
import { useDrawerToggle, useDrawerFilters } from '@minecraft.drawer';
import { saveFilterUtilities, useAdditionalInitialFilters } from '@blocs.filter';
import { FilterValues } from '../shared/types';
import { isEqualCustomizer } from '../shared/utils';
import { FilterDrawerProps } from './shared/types';
import { useFilterDrawerCounts } from './shared/hooks';
import { initialFilters } from './shared/config';
import FilterDrawer from './FilterDrawer';

const FilterDrawerContainer: FC<FilterDrawerProps> = ({
  filterPermissions = {},
  isSubmissionContext = false,
  type,
  roleId,
  submissionStatusCode,
  sessionStorageKey,
  accountCountryCode,
  accountOrgFeatures,
  appliedFilters: primaryAppliedFilters,
  appliedQueryInput,
  onFiltersChange,
  getAdditionalAutofillFilters,
  isTalentScout,
}) => {
  const { isDrawerOpened } = useDrawerToggle();
  const { retainedFilters, handleDrawerRateinedFiltersChange } = useDrawerFilters();

  const getAdditionalInitialFilters = useAdditionalInitialFilters();

  const appliedFilters =
    retainedFilters ||
    primaryAppliedFilters ||
    (sessionStorageKey
      ? saveFilterUtilities.getSavedFiltersForThisSession({ storageKey: sessionStorageKey }) || {}
      : {});

  const handleSubmit = (values: FilterValues): void => {
    if (_isEqualWith(appliedFilters, values, isEqualCustomizer)) return;

    handleDrawerRateinedFiltersChange(values);
    onFiltersChange(values);

    if (sessionStorageKey) {
      saveFilterUtilities.saveFiltersForThisSession({ storageKey: sessionStorageKey, filters: values });
    }
  };

  const { refetchProfileFilterData } = useFilterDrawerCounts({
    isSubmissionContext,
    roleId,
    submissionStatusCode,
    appliedQueryInput,
    isTalentScout,
  });

  const additionalInitialFilters = useMemo(
    () =>
      getAdditionalInitialFilters({
        isSubmissionContext,
        accountOrgFeatures,
        appliedFilters,
      }),
    [accountOrgFeatures, appliedFilters]
  );

  const initialValue = useMemo(
    () => ({ ...initialFilters[type], ...appliedFilters, ...additionalInitialFilters[type] }),
    [appliedFilters, additionalInitialFilters[type]]
  );

  return (
    isDrawerOpened && (
      <Formik initialValues={initialValue} onSubmit={handleSubmit}>
        <FilterDrawer
          appliedQueryInput={appliedQueryInput}
          refetchProfileFilterData={refetchProfileFilterData}
          type={type}
          filterPermissions={filterPermissions}
          isSubmissionContext={isSubmissionContext}
          roleId={roleId}
          accountCountryCode={accountCountryCode}
          additionalInitialFilters={additionalInitialFilters}
          getAdditionalAutofillFilters={getAdditionalAutofillFilters}
          testId="bloc-filter-advanced"
        />
      </Formik>
    )
  );
};

export default FilterDrawerContainer;
